import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogProps,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { useReducer } from "react";

import {
  MARKETING_PRESENTATION_REASON_LABELS,
  REQUOTE_REASONS,
} from "../constants";
import { Quote } from "../types";
import { getOtherReasonParts, getProductDisplayName } from "../utils";

type Props = {
  quote: Pick<
    Quote,
    "productType" | "requoteReason" | "requoteRequired" | "currentStatus"
  >;
  onClose: VoidFunction;
  onSave: (quote: QuoteBody) => void;
  statusOptions?: Option[];
  dialogProps?: Partial<Omit<DialogProps, "onClose">>;
  isLoading: boolean;
};

function EditQuoteDialog({
  quote,
  onClose,
  onSave,
  statusOptions,
  dialogProps,
  isLoading,
}: Props) {
  const [state, dispatch] = useReducer(reducer, initialState, (init) => {
    const { reason: requoteReason, otherReason: requoteOtherReason } =
      getOtherReasonParts(REQUOTE_REASONS, quote.requoteReason);

    return {
      ...init,
      requoteReason,
      requoteOtherReason,
      exclusionReason: quote.currentStatus?.exclusionReason || "",
      otherReason: quote.currentStatus?.otherReason || "",
      requoteNeeded: quote.requoteRequired,
      status: quote.currentStatus?.value || "",
    };
  });
  const {
    changed,
    status,
    exclusionReason,
    otherReason,
    requoteNeeded,
    requoteReason,
    requoteOtherReason,
  } = state;

  const handleSave = () => {
    const resolvedExclusionReason =
      exclusionReason === "other" ? otherReason : exclusionReason;
    const resolvedRequoteReason =
      requoteReason === "other" ? requoteOtherReason : requoteReason;
    const quote: QuoteBody = {
      status: status || null,
      exclusionReason: resolvedExclusionReason || null,
      requoteRequired: requoteNeeded,
      requoteReason: resolvedRequoteReason || null,
    };
    onSave(quote);
  };
  const showExclusionReason = status === "excludedMarketing";

  function shouldDisableSave() {
    if (!changed) {
      return true;
    }
    if (!status && requoteNeeded === null) {
      return true;
    }
    if (status === "excludedMarketing" && !exclusionReason) {
      return true;
    }
    if (exclusionReason === "other" && !otherReason) {
      return true;
    }
    if (requoteNeeded && !requoteReason) {
      return true;
    }
    if (requoteReason === "other" && !requoteOtherReason) {
      return true;
    }
    return false;
  }

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth {...dialogProps}>
      <DialogTitle variant="h6" gutterBottom={false}>
        Edit - {getProductDisplayName(quote.productType)}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
          <FormControl fullWidth>
            <InputLabel id="edit-quote-status-select-label">Status</InputLabel>
            <Select
              labelId="edit-quote-status-select-label"
              value={status}
              onChange={(event) => {
                dispatch({
                  type: ACTION_TYPES.SET_STATUS,
                  payload: event.target.value,
                });
              }}
              label="Status"
            >
              {quote.currentStatus ? (
                <MenuItem
                  sx={{ display: "none" }}
                  value={quote.currentStatus.value}
                  disabled
                >
                  {quote.currentStatus.text}
                </MenuItem>
              ) : null}
              {statusOptions?.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.text}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {showExclusionReason && (
            <>
              <FormControl fullWidth>
                <InputLabel id="exclusion-reason-label">
                  Exclusion Reason
                </InputLabel>
                <Select
                  labelId="exclusion-reason-label"
                  value={exclusionReason}
                  onChange={(event) => {
                    dispatch({
                      type: ACTION_TYPES.SET_EXCLUSION_REASON,
                      payload: event.target.value,
                    });
                  }}
                  label="Exclusion Reason"
                >
                  <MenuItem value="" disabled>
                    Reason
                  </MenuItem>
                  {Object.entries(MARKETING_PRESENTATION_REASON_LABELS).map(
                    ([key, value]) => (
                      <MenuItem value={key} key={key}>
                        {value}
                      </MenuItem>
                    ),
                  )}
                </Select>
              </FormControl>

              {exclusionReason === "other" && (
                <TextField
                  fullWidth
                  label="Other Reason"
                  value={otherReason ?? ""}
                  onChange={(e) =>
                    dispatch({
                      type: ACTION_TYPES.SET_OTHER_REASON,
                      payload: e.target.value,
                    })
                  }
                />
              )}
            </>
          )}

          <FormControlLabel
            control={
              <Checkbox
                checked={requoteNeeded ?? false}
                onChange={(event) => {
                  dispatch({
                    type: ACTION_TYPES.SET_REQUOTE_NEEDED,
                    payload: event.target.checked,
                  });
                }}
              />
            }
            label="Requote Needed"
          />

          {requoteNeeded && (
            <>
              <FormControl fullWidth>
                <InputLabel id="requote-reason-label">Reason</InputLabel>
                <Select
                  labelId="requote-reason-label"
                  value={requoteReason}
                  onChange={(event) => {
                    dispatch({
                      type: ACTION_TYPES.SET_REQUOTE_REASON,
                      payload: event.target.value,
                    });
                  }}
                  label="Reason"
                >
                  <MenuItem value="" disabled>
                    Reason
                  </MenuItem>
                  {Object.entries(REQUOTE_REASONS).map(([key, value]) => (
                    <MenuItem value={key} key={key}>
                      {value}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {requoteReason === "other" && (
                <TextField
                  fullWidth
                  label="Other Reason"
                  value={requoteOtherReason}
                  onChange={(e) =>
                    dispatch({
                      type: ACTION_TYPES.SET_REQUOTE_OTHER_REASON,
                      payload: e.target.value,
                    })
                  }
                />
              )}
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="text">
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="text"
          loading={isLoading}
          disabled={shouldDisableSave()}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

type QuoteBody = {
  status: string | null;
  exclusionReason: string | null;
  requoteRequired: boolean | null;
  requoteReason: string | null;
};

type Option = { value: string; text: string };

const ACTION_TYPES = {
  SET_STATUS: "SET_STATUS",
  SET_EXCLUSION_REASON: "SET_EXCLUSION_REASON",
  SET_OTHER_REASON: "SET_OTHER_REASON",
  SET_REQUOTE_NEEDED: "SET_REQUOTE_NEEDED",
  SET_REQUOTE_REASON: "SET_REQUOTE_REASON",
  SET_REQUOTE_OTHER_REASON: "SET_REQUOTE_OTHER_REASON",
} as const;

type ActionGenerator<T, K> = {
  type: T;
  payload: K;
};

type ActionsGenerator<T> = T extends keyof typeof ACTION_TYPES
  ? ActionGenerator<T, string>
  : never;

type Action =
  | ActionsGenerator<
      Exclude<keyof typeof ACTION_TYPES, typeof ACTION_TYPES.SET_REQUOTE_NEEDED>
    >
  | ActionGenerator<typeof ACTION_TYPES.SET_REQUOTE_NEEDED, boolean>;

type State = {
  changed: boolean;
  status: string;
  exclusionReason: string;
  otherReason: string;
  requoteNeeded: boolean | null;
  requoteReason: string;
  requoteOtherReason: string;
};

const initialState: State = {
  changed: false,
  status: "",
  exclusionReason: "",
  otherReason: "",
  requoteNeeded: null,
  requoteReason: "",
  requoteOtherReason: "",
};

function reducer(state: State, action: Action): State {
  const changedState = { ...state, changed: true };
  switch (action.type) {
    case ACTION_TYPES.SET_STATUS:
      return {
        ...changedState,
        status: action.payload,
        exclusionReason: "",
        otherReason: "",
      };
    case ACTION_TYPES.SET_EXCLUSION_REASON:
      return {
        ...changedState,
        exclusionReason: action.payload,
        otherReason: action.payload !== "other" ? "" : state.otherReason,
      };
    case ACTION_TYPES.SET_OTHER_REASON:
      return {
        ...changedState,
        otherReason: action.payload,
      };
    case ACTION_TYPES.SET_REQUOTE_NEEDED:
      return {
        ...changedState,
        requoteNeeded: action.payload,
        requoteReason: action.payload ? state.requoteReason : "",
        requoteOtherReason: action.payload ? state.requoteOtherReason : "",
      };
    case ACTION_TYPES.SET_REQUOTE_REASON:
      return {
        ...changedState,
        requoteReason: action.payload,
        requoteOtherReason:
          action.payload !== "other" ? "" : state.requoteOtherReason,
      };
    case ACTION_TYPES.SET_REQUOTE_OTHER_REASON:
      return {
        ...changedState,
        requoteOtherReason: action.payload,
      };
    default:
      return state;
  }
}

export default EditQuoteDialog;
